import { useState, useEffect, useRef } from "react";
import { CallbackForm } from "@/components/CallbackForm";
import { Phone, Clock, Shield, CheckCircle, Wrench, Key, DoorOpen, Settings, Star, Users, Blinds, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import SEO from "@/components/SEO";
import logo from "@/assets/logo.jpg";
import heroImage from "@/assets/serrurier-hero-new.jpg";
import brandsImage from "@/assets/brands/serrures-marques.jpg";

// Insurance logos
import agfLogo from "@/assets/insurances/agf.png";
import macifLogo from "@/assets/insurances/macif.jpg";
import groupamaLogo from "@/assets/insurances/groupama.jpg";
import maifLogo from "@/assets/insurances/maif.jpg";
import matmutLogo from "@/assets/insurances/matmut.jpg";
import mmaLogo from "@/assets/insurances/mma.jpg";
import swisslifeLogo from "@/assets/insurances/swisslife.jpg";
import gmfLogo from "@/assets/insurances/gmf.png";
import allianzLogo from "@/assets/insurances/allianz.jpg";

const PHONE_NUMBER = "07 66 00 91 89";
const PHONE_LINK = "tel:0766009189";

const insuranceLogos = [
  { src: agfLogo, alt: "AGF Assurance" },
  { src: macifLogo, alt: "MACIF" },
  { src: groupamaLogo, alt: "Groupama" },
  { src: maifLogo, alt: "MAIF" },
  { src: matmutLogo, alt: "Matmut" },
  { src: mmaLogo, alt: "MMA" },
  { src: swisslifeLogo, alt: "Swiss Life" },
  { src: gmfLogo, alt: "GMF" },
  { src: allianzLogo, alt: "Allianz" },
];

const useCountUp = (end: number, duration: number = 2000) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            setCount(Math.floor(eased * end));
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return { count, ref };
};

const LandingAds = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCallbackFormOpen, setIsCallbackFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    problemType: "",
  });
  
  const { count: interventionCount, ref: counterRef } = useCountUp(500, 2000);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("https://formsubmit.co/serruriertolstoi@gmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          name: formData.name,
          phone: formData.phone,
          problemType: formData.problemType,
          _subject: `🔴 URGENCE Google Ads - ${formData.problemType}`,
          _template: "table",
          _captcha: "false",
        }).toString(),
      });

      if (response.ok) {
        toast({
          title: "Demande envoyée !",
          description: "Nous vous rappelons dans les 5 minutes.",
        });
        setFormData({ name: "", phone: "", problemType: "" });
      } else {
        throw new Error("Erreur d'envoi");
      }
    } catch {
      toast({
        title: "Erreur",
        description: "Veuillez nous appeler directement au " + PHONE_NUMBER,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Serrurier Urgence Villeurbanne Lyon | Intervention 20 min | 07 66 00 91 89"
        description="Serrurier d'urgence agréé toutes assurances. Intervention en moins de 20 minutes à Villeurbanne et Lyon. Appelez maintenant le 07 66 00 91 89."
        keywords="serrurier urgence, serrurier villeurbanne, serrurier lyon, ouverture porte, dépannage serrure"
        noindex={true}
      />

      {/* Header simplifié */}
      <header className="bg-secondary sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <img src={logo} alt="Tolstoï Serrurier" className="h-12 w-auto" />
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 bg-primary/20 px-3 py-1 rounded-full">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Intervention &lt; 20 min</span>
            </div>
            <a
              href={PHONE_LINK}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-bold hover:bg-primary/90 transition-all animate-pulse"
            >
              <Phone className="h-5 w-5" />
              <span>{PHONE_NUMBER}</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative min-h-[80vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        <div className="relative z-10 container mx-auto px-4 py-16 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-primary px-4 py-2 rounded-full mb-6 animate-fade-in">
            <Shield className="h-5 w-5" />
            <span className="font-semibold">Agréé Toutes Assurances</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 animate-fade-in">
            Serrurier d'Urgence
            <br />
            <span className="text-primary">Lyon et son agglomération</span>
          </h1>
          <p className="text-xl md:text-2xl mb-4 opacity-90 animate-fade-in">
            Intervention en moins de <strong className="text-primary">20 minutes</strong> • 24h/24 • 7j/7
          </p>
          
          {/* Counter Badge */}
          <div 
            ref={counterRef}
            className="inline-flex items-center gap-2 bg-green-600/90 backdrop-blur-sm px-5 py-2 rounded-full mb-8 animate-fade-in"
          >
            <Users className="h-5 w-5 text-white" />
            <span className="font-bold text-white tabular-nums">+ {interventionCount} interventions réalisées</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in mb-10">
            <a
              href={PHONE_LINK}
              className="inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-xl text-xl font-bold hover:bg-primary/90 transition-all hover:scale-105 shadow-2xl"
            >
              <Phone className="h-6 w-6" />
              Appeler maintenant : {PHONE_NUMBER}
            </a>
            <button
              onClick={() => setIsCallbackFormOpen(true)}
              className="inline-flex items-center justify-center gap-3 bg-white/20 backdrop-blur-sm text-white border-2 border-white px-8 py-4 rounded-xl text-xl font-bold hover:bg-white/30 transition-all"
            >
              Demander un rappel immédiat
            </button>
          </div>

          {/* Glass Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto animate-fade-in">
            {[
              { icon: Clock, title: "< 20 min", desc: "Intervention rapide" },
              { icon: Shield, title: "Assurances", desc: "Agréé toutes compagnies" },
              { icon: CheckCircle, title: "Devis gratuit", desc: "Sans engagement" },
              { icon: Wrench, title: "Certifié", desc: "Professionnel qualifié" },
            ].map((item, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center p-4 md:p-6 rounded-xl backdrop-blur-md bg-white/15 border border-white/20 shadow-lg hover:bg-white/25 transition-all"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/80 rounded-full flex items-center justify-center mb-3">
                  <item.icon className="h-6 w-6 md:h-7 md:w-7 text-primary-foreground" />
                </div>
                <h3 className="font-bold text-base md:text-lg text-white">{item.title}</h3>
                <p className="text-white/80 text-xs md:text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-10 bg-muted overflow-hidden">
        <div className="container mx-auto px-4 mb-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground flex items-center justify-center gap-2">
            <Star className="h-6 w-6 text-primary fill-primary" />
            Avis de nos clients
            <Star className="h-6 w-6 text-primary fill-primary" />
          </h2>
        </div>
        
        {/* Scrolling testimonials */}
        <div className="relative">
          <div className="flex animate-marquee-testimonials">
            {[
              { name: "Marie L.", text: "Intervention en 15 min, très professionnel. Je recommande !", rating: 5 },
              { name: "Pierre D.", text: "Porte claquée à 23h, dépannage rapide et prix correct.", rating: 5 },
              { name: "Sophie M.", text: "Excellent service, serrurier compétent et aimable.", rating: 5 },
              { name: "Jean-Paul R.", text: "Changement de serrure après cambriolage, travail impeccable.", rating: 5 },
              { name: "Isabelle T.", text: "Réactif et efficace. Merci pour le dépannage express !", rating: 5 },
              { name: "Thomas B.", text: "Prix transparent, pas de mauvaise surprise. Top !", rating: 5 },
              { name: "Marie L.", text: "Intervention en 15 min, très professionnel. Je recommande !", rating: 5 },
              { name: "Pierre D.", text: "Porte claquée à 23h, dépannage rapide et prix correct.", rating: 5 },
              { name: "Sophie M.", text: "Excellent service, serrurier compétent et aimable.", rating: 5 },
              { name: "Jean-Paul R.", text: "Changement de serrure après cambriolage, travail impeccable.", rating: 5 },
              { name: "Isabelle T.", text: "Réactif et efficace. Merci pour le dépannage express !", rating: 5 },
              { name: "Thomas B.", text: "Prix transparent, pas de mauvaise surprise. Top !", rating: 5 },
            ].map((testimonial, i) => (
              <div
                key={i}
                className="flex-shrink-0 mx-3 bg-card rounded-xl p-5 shadow-lg border border-border"
                style={{ width: "280px" }}
              >
                <div className="flex gap-1 mb-2">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 text-primary fill-primary" />
                  ))}
                </div>
                <p className="text-foreground text-sm mb-3 italic">"{testimonial.text}"</p>
                <p className="font-semibold text-muted-foreground text-sm">— {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Logos Carousel */}
      <section className="py-12 bg-secondary overflow-hidden">
        <div className="container mx-auto px-4 mb-8 text-center">
          <div className="inline-flex items-center gap-2 bg-primary px-4 py-2 rounded-full mb-4">
            <Shield className="h-5 w-5 text-primary-foreground" />
            <span className="font-semibold text-primary-foreground">Agréé Toutes Assurances</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-secondary-foreground">
            Nous travaillons avec toutes les compagnies
          </h2>
        </div>
        
        {/* Infinite scrolling carousel */}
        <div className="relative">
          <div className="flex animate-marquee">
            {[...insuranceLogos, ...insuranceLogos].map((logo, i) => (
              <div
                key={i}
                className="flex-shrink-0 mx-4 bg-white rounded-xl p-4 shadow-md flex items-center justify-center"
                style={{ width: "150px", height: "80px" }}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 text-foreground">
            Nos Services d'Urgence
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {[
              { icon: DoorOpen, title: "Ouverture de porte", desc: "Porte claquée ou bloquée" },
              { icon: Key, title: "Changement de serrure", desc: "Mise en sécurité rapide" },
              { icon: Settings, title: "Réparation serrure", desc: "Tous types de serrures" },
              { icon: Clock, title: "Urgence 24/7", desc: "Intervention jour et nuit" },
              { icon: Blinds, title: "Volet roulant", desc: "Réparation et installation" },
              { icon: ShieldCheck, title: "Porte blindée & garage", desc: "Sécurisation renforcée" },
            ].map((service, i) => (
              <div
                key={i}
                className="p-6 bg-card rounded-xl border border-border hover:border-primary transition-colors text-center group"
              >
                <service.icon className="h-10 w-10 text-primary mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-bold text-foreground mb-2">{service.title}</h3>
                <p className="text-muted-foreground text-sm">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-12 bg-muted">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-foreground">
            Nous installons et réparons toutes les marques
          </h2>
          <div className="max-w-4xl mx-auto bg-white rounded-2xl p-6 shadow-lg">
            <img
              src={brandsImage}
              alt="Marques de serrures : Fichet, Bricard, Vachette, Pollux, Picard, Mul-T-Lock, Mottura, Reelax"
              className="w-full h-auto rounded-xl"
            />
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 bg-gradient-to-br from-primary/10 to-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Être rappelé en 5 minutes
              </h2>
              <p className="text-muted-foreground">
                Laissez-nous vos coordonnées, un technicien vous rappelle immédiatement.
              </p>
            </div>
            <form
              onSubmit={handleSubmit}
              className="bg-card p-8 rounded-2xl shadow-xl border border-border space-y-6"
            >
              <div className="space-y-2">
                <Label htmlFor="name">Votre nom</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Jean Dupont"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Votre téléphone</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="06 12 34 56 78"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="problem">Type de problème</Label>
                <Select
                  value={formData.problemType}
                  onValueChange={(value) => setFormData({ ...formData, problemType: value })}
                  required
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Sélectionnez votre problème" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="porte-claquee">Porte claquée</SelectItem>
                    <SelectItem value="cle-cassee">Clé cassée dans la serrure</SelectItem>
                    <SelectItem value="serrure-bloquee">Serrure bloquée</SelectItem>
                    <SelectItem value="cambriolage">Suite à un cambriolage</SelectItem>
                    <SelectItem value="changement-serrure">Changement de serrure</SelectItem>
                    <SelectItem value="volet-roulant">Volet roulant bloqué</SelectItem>
                    <SelectItem value="porte-blindee">Porte blindée / garage</SelectItem>
                    <SelectItem value="autre">Autre problème</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 text-lg font-bold bg-primary hover:bg-primary/90"
              >
                {isSubmitting ? "Envoi en cours..." : "Être rappelé en 5 minutes"}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Ou appelez directement le{" "}
                <a href={PHONE_LINK} className="text-primary font-bold hover:underline">
                  {PHONE_NUMBER}
                </a>
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Footer simplifié */}
      <footer className="bg-secondary py-8">
        <div className="container mx-auto px-4 text-center text-secondary-foreground">
          <a
            href={PHONE_LINK}
            className="inline-flex items-center gap-2 text-2xl font-bold text-primary mb-4"
          >
            <Phone className="h-6 w-6" />
            {PHONE_NUMBER}
          </a>
          <p className="text-secondary-foreground/70 mb-4">
            Serrurier d'urgence agréé assurances • Villeurbanne & Lyon • 24h/24 7j/7
          </p>
          <Link to="/mentions-legales" className="text-secondary-foreground/50 hover:text-primary text-sm">
            Mentions légales
          </Link>
          <p className="mt-4 text-secondary-foreground/50 text-sm">
            © {new Date().getFullYear()} Tolstoï Serrurier. Tous droits réservés.
          </p>
        </div>
      </footer>

      {/* Floating Call Button */}
      <a
        href={PHONE_LINK}
        className="fixed bottom-6 right-6 z-50 bg-primary text-primary-foreground p-4 rounded-full shadow-2xl hover:scale-110 transition-transform animate-pulse flex items-center justify-center"
        aria-label="Appeler maintenant"
      >
        <Phone className="h-8 w-8" />
      </a>

      {/* Callback Form Popup */}
      <CallbackForm 
        open={isCallbackFormOpen} 
        onOpenChange={setIsCallbackFormOpen} 
      />

      {/* CSS for marquee animation */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
        @keyframes marquee-testimonials {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee-testimonials {
          animation: marquee-testimonials 30s linear infinite;
        }
      `}</style>
    </>
  );
};

export default LandingAds;
